import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Tooltip, IconButton } from '@mui/material';
import { GridActionsCellItem, GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from '@mui/x-data-grid-pro';
import CreateIcon from '@mui/icons-material/Create';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    BreadCrumbs,
    PageTitleWithToolTip,
    BulkModal,
    AddButton,
    DataGridWithExpansionPanel,
    SubTable
} from './Common';
import {
    BadgeWithLinkedText,
    StyledCellLink,
} from './Common/DataGrid/CellComponents';
import { getDateRangeFilterOperator } from './Common/DataGrid/FilterComponents';
import Routes from '../routes';
import CloseWorkOrderModal from "./WorkOrder/Components/CloseWorkOrderModal";
import AddServiceEventModal from './WorkOrder/Components/AddServiceEventModal';
import UserInfo from '../UserInfo';
import { Translate, TranslateW } from '../Utils/Languages/Translate';
import { isMobile, isDesktop, fetchFilterValues } from '../utils';
import {
    getFilterOptions,
    dataGridContainerProps,
    text_commonCellProps,
    select_commonCellProps,
    EmptyActionIcon
} from './Common/DataGrid/utils';
import { canCloseWorkOrder } from './WorkOrder/utils';
import { Grey2 } from '../color_utils';
import {
    useBreadCrumbs,
    useNavigate,
    useBulkUpdates
} from '../hooks';
import './Common/Table/Table.css';

const commonSubtableCellProps = { disableColumnMenu: true, disableReorder: true, sortable: false, flex: 1 };
const batchUpdateType = "workOrders";
const dateRangeFilterOperator = getDateRangeFilterOperator({});

const WorkOrders = ({ history }) => {
    const [filters, setFilters] = useState([]);
    const [actionModal, setActionModal] = useState(false);
    const [modalPayload, setModalPayload] = useState({});
    const [refreshNeeded, setRefreshNeeded] = useState(false);
    const [batchUpdateModelOpen, setBatchUpdateModelOpen] = useState(false);
    const [expandedRowIds, setExpandedRowIds] = useState([]);
    const [tableFilters, setTableFilters] = useState({});

    const { isLoading, BatchUpdateExport, BatchUpdateImport } = useBulkUpdates();

    const navigate = useNavigate({ pageName: 'WorkOrders' });

    useBreadCrumbs({
        path: { id: 'WorkOrders', text: 'Work Orders', link: history.location.pathname, reset: true }
    });
    
    const subTableColumns = [
        {
            ...commonSubtableCellProps,
            headerName: "SERVICE EVENT ID",
            field: "idString",
            renderCell: ({ row: { id } }) => (
                <StyledCellLink
                    primaryLink
                    path={`/ServiceEvent/${id}`}
                    text={id}
                    from={window.location.pathname}
                />
            )
        },
        {
            ...commonSubtableCellProps,
            headerName: "TYPE",
            field: "type",
        },
        {
            ...commonSubtableCellProps,
            headerName: "ASSET NAME",
            field: "assetName",
        },
        {
            ...commonSubtableCellProps,
            headerName: "PRIMARY TECH",
            field: "primaryTechnicianName",
        },
        {
            ...commonSubtableCellProps,
            headerName: "SERVICE DATE",
            field: "serviceDate",
        }
    ];

    const columns = [
        {
            field: 'id',
            headerName: 'Work Order ID',
            ...text_commonCellProps,
            renderCell: ({ row: { status, id, enabled } }) => (
                <BadgeWithLinkedText
                    statusProperty={enabled ? status : 'deleted'}
                    text={id}
                    to={{
                        pathname: `/WorkOrder/${id}`,
                        state: {
                            from: window.location.pathname
                        }
                    }}
                />
            )
        },
        {
            field: 'locationName',
            headerName: 'Location',
            ...select_commonCellProps,
            // US 16741: Location hyperlink (secondary/underlink) enabled at all breakpoints (was desktop-only).
            renderCell: ({ row: { locationName, locationID } }) => (
                <StyledCellLink
                    blueLinkStyle
                    path={`/Location/${locationID}`}
                    text={locationName}
                    from={window.location.pathname}
                />
            )
        },
        {
            field: 'workOrderNumber',
            headerName: 'Customer Work Order #',
            ...text_commonCellProps,
        },
        {
            field: 'state',
            headerName: 'State',
            initiallyVisible: false,
            ...select_commonCellProps,
        },
        {
            field: 'city',
            headerName: 'City',
            initiallyVisible: false,
            ...select_commonCellProps,
        },
        {
            field: 'scheduledDate',
            headerName: 'Scheduled Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
        },
        {
            field: 'createdOn',
            headerName: 'Created On',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
            initiallyVisible: false
        },
        {
            field: 'status',
            headerName: 'Status',
            initiallyVisible: false,
            ...select_commonCellProps,
        },
        {
            field: 'statusReason',
            headerName: 'Status Reason',
            ...select_commonCellProps,
        },
        {
            field: 'servicer',
            headerName: 'Service Provider',
            ...select_commonCellProps,
        },
        {
            field: 'type',
            headerName: 'Work Type',
            ...select_commonCellProps,
        },
        {
            field: 'dueDate',
            headerName: 'Due Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
            initiallyVisible: false
        },
        {
            field: 'regionDivision',
            headerName: 'Region/Division',
            initiallyVisible: false,
            ...select_commonCellProps,
        },
        {
            ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
            width: 150,
            renderCell: ({ row }) => {
                const quickActions = [];
                const pushEmptyIcon = () => quickActions.push(
                    <IconButton disabled sx={{ width: "30px", height: "30px", padding: "0px", margin: "0px" }} />
                );

                const canEditWorkOrder = row.assignedLocation && row.isDeleted === "No" && !UserInfo.isReadOnly() && (
                    (UserInfo.isSystemOwner() && (UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator() || UserInfo.isRegionalAdministrator() || UserInfo.isTechnicianManager() || UserInfo.isTechnician())) ||
                    (UserInfo.isServiceProvider() && (UserInfo.isTenantAdministrator() || UserInfo.isTechnician())) ||
                    (UserInfo.isSystemAdministrator())
                );

                if (canEditWorkOrder) {
                    quickActions.push(
                        <GridActionsCellItem
                            icon={
                                <Tooltip
                                    title="Edit Work Order"
                                >
                                    <CreateIcon sx={{ color: Grey2 }} />
                                </Tooltip>
                            }
                            onClick={() => editWorkOrder(row)}
                        />
                    );
                } else { pushEmptyIcon() }
                
                if (
                    !UserInfo.isReadOnly()
                    && row.isDeleted === "No"
                    && (row.status && row.status.toLowerCase() === "open")
                    && isDesktop
                ) {
                    quickActions.push(
                        <GridActionsCellItem
                            sx={{display: { xs: 'none', md: 'inline-flex' }}}
                            icon={
                                <Tooltip
                                    title="Create Service Event"
                                >
                                    <AddIcon sx={{ color: Grey2 }} />
                                </Tooltip>
                            }
                            onClick={(e) => openModal(e, row, "AddServiceEvent")}
                        />
                    )
                } else { pushEmptyIcon() }

                if (canCloseWorkOrder(row)) {
                    quickActions.push(
                        <GridActionsCellItem
                            icon={
                                <Tooltip
                                    title="Close Work Order"
                                >
                                    <CloseIcon sx={{ color: Grey2 }} />
                                </Tooltip>
                            }
                            onClick={(e) => openModal(e, row, "Close")}
                        />
                    );
                } else { pushEmptyIcon() }

                if (row.hasServiceEvents) {
                    quickActions.push(
                        <CustomDetailPanelToggle row={row} />
                    );
                } else { pushEmptyIcon() }

                return quickActions;
            }
        }
    ];

    const renderCreateWorkOrderButton = (
        !UserInfo.isReadOnly()
    );

    const renderDeletedColumn = (
        UserInfo.isSystemAdministrator()
        || UserInfo.isSuperTenantAdmin()
        || UserInfo.isTenantAdministrator()
        || UserInfo.isTechnicianManager()
    );

    const fetchData = async () => {
        const data = await fetchFilterValues(Routes.WorkOrder.TableFilters());
        setFilters(data);
    };

    const openModal = (e, workOrder, modalType) => {
        e.stopPropagation();
        const payload = {
            ...workOrder,
            ID: workOrder.id,
        };
        console.log('work order data', payload);
        setModalPayload(payload);
        setActionModal(modalType);
    };

    const editWorkOrder = ({ id }) => navigate.push(`/EditWorkOrder/${id}`);

    const handleSubtableOpen = ({ id }) => {
        let newExpandedRowIds = [];
        // if user clicks on open row, close row
        if (expandedRowIds.includes(id)) {
            newExpandedRowIds = expandedRowIds.filter(rowId => rowId !== id);
        } else {
            // row is closed, open row
            newExpandedRowIds = [...expandedRowIds, id];
        }

        return setExpandedRowIds(newExpandedRowIds);
    };

    const CustomDetailPanelToggle = useCallback(({ row }) => {
        const isOpen = expandedRowIds.includes(row.id);
        return (
            <Tooltip
                title={
                    isOpen
                        ? "Hide Service Events"
                        : "View Service Events"
                }
            >
                <IconButton
                    size="medium"
                    tabIndex={-1}
                >
                    <ExpandMoreIcon
                        sx={(theme) => ({
                            color: Grey2,
                            transform: `rotateZ(${isOpen ? 180 : 0}deg)`,
                            transition: theme.transitions.create('transform', {
                                duration: theme.transitions.duration.complex
                            }),
                        })}
                        fontSize="inherit"
                    />
                </IconButton>
            </Tooltip>
        );
    }, [expandedRowIds]);

    const getDetailPanelContent = useCallback(({ row }) => {
        if (row.hasServiceEvents) {
            return (
                <div className='subtable'>
                    <SubTable
                        parentEntityId={row.id}
                        columns={subTableColumns}
                    />
                </div>
            );
        }

        return null;
    }, [expandedRowIds.length]);

    const handleOnDetailPanelExpandedRowIdsChange = (newIds) => {
        if (newIds.length > 0) {
            setExpandedRowIds([newIds[newIds.length - 1]]);
        } else {
            setExpandedRowIds([]);
        }
    }

    useEffect(() => {
        fetchData();
    }, []);

    if (renderDeletedColumn) {
        columns.splice(10, 0, {
            field: 'isDeleted',
            headerName: 'Deleted',
            sortable: false,
            initiallyVisible: false,
            ...select_commonCellProps,
            renderCell: ({ row: { enabled } }) => enabled ? "No" : "Yes"
        });
    }

    return (
        <>
            {<BreadCrumbs
                sx={{
                    display: (UserInfo.isProduction || isDesktop) ? { xs: 'none', lg: 'flex'} : 'none'
                }}
                path={[
                    { text: Translate('assetdetailsmaintab.workOrder') }
                ]}
            />}
            <Grid
                container
                direction="row"
                alignItems="center"
            >
                <Grid item xs={6} md={6} lg={6} sx={{ marginTop: { xs: '24px', md: '0px' }, px: { xs: '12px', md: '0px' } }}>
                    <PageTitleWithToolTip
                        pageTitle={Translate('assetdetailsmaintab.workOrder')}
                        toolTipText="A Work Order is the first part in recording service on an asset.
                            Trakref uses a work order as unique way to link all service events together for all the work done at a location in Trakref.
                        "
                    />
                </Grid>

                {UserInfo.isSystemAdministrator() && (
                    <>
                        <Grid item sx={{ marginLeft: "auto", display: { xs: 'none', lg: 'inline-flex' } }}>
                            <Tooltip title="Export Work Orders Template">
                                <IconButton
                                    onClick={(_) => BatchUpdateExport(
                                        batchUpdateType,
                                        tableFilters,
                                    )}
                                >
                                    <img
                                        src="/images/batchUpdateExport.svg"
                                        alt={TranslateW("BatchUpdateExport")}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item sx={{ marginRight: "1%", display: { xs: 'none', lg: 'inline-flex' } }}>
                            <Tooltip title="Bulk Import Work Orders">
                                <IconButton
                                    onClick={_ => setBatchUpdateModelOpen(true)}
                                >
                                    <img
                                        src="/images/batchUpdateImport.svg"
                                        alt={TranslateW("BatchUpdateExport")}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </>
                )}

                {renderCreateWorkOrderButton && (
                    <Grid
                        item
                        align="right"
                        sx={{
                            marginLeft: { xs: '12px', md: 'auto', lg: UserInfo.isSystemAdministrator() ? 0 : 'auto' },
                            marginRight: { xs: '12px', md: 0 },
                            width: { xs: '100%', md: 'auto' },
                            height: { xs: '42px', lg: 'none' },
                            marginTop: { xs: '36px', md: 'auto' },
                        }}
                    >
                        <AddButton
                            sx={{
                                height: { xs: '100%', lg: 'none' },
                                width: { xs: '100%', md: 'fit-content' }
                            }}
                            text={Translate("createWorkOrder")}
                            onClick={() => navigate.push('/CreateWorkOrder')}
                        />
                    </Grid>
                )}

                <Grid item {...dataGridContainerProps}>
                    <DataGridWithExpansionPanel
                        url={Routes.WorkOrder.WorkOrderTable()}
                        subTableColumns={subTableColumns}
                        hideExportButton={!UserInfo.isProduction && !isDesktop}
                        exportButtonURL={Routes.WorkOrder.Export()}
                        rowCount={isMobile ? 5 : undefined}
                        exportButtonAlt="Export Work Orders (.csv)"
                        filterOptions={filters}
                        refresh={refreshNeeded}
                        noResultText="Oops...It is empty here..."
                        noResultText2="Create a new Work Order or change your search criterias."
                        tableId={"work-order-table"}
                        columns={columns}
                        getDetailPanelContent={getDetailPanelContent}
                        getDetailPanelHeight={() => "auto"}
                        detailPanelExpandedRowIds={expandedRowIds}
                        columnDependencies={expandedRowIds}
                        onDetailPanelExpandedRowIdsChange={handleOnDetailPanelExpandedRowIdsChange}
                        setTableFilters={setTableFilters}
                    />
                </Grid>

            </Grid>

            <CloseWorkOrderModal
                payload={modalPayload}
                modalOpen={actionModal === 'Close'}
                onClose={() => setActionModal(null)}
                refreshRequired={() => setRefreshNeeded(!refreshNeeded)}
            />

            <AddServiceEventModal
                payload={modalPayload}
                modalOpen={actionModal === 'AddServiceEvent'}
                onClose={() => setActionModal(null)}
                history={history}
            />

            <BulkModal
                open={batchUpdateModelOpen}
                onClose={() => setBatchUpdateModelOpen(false)}
                onClick={async (excelFile) => {
                    await BatchUpdateImport(batchUpdateType, excelFile);
                    setBatchUpdateModelOpen(false);
                }}
                loading={isLoading}
            />
        </>
    );

};

export default WorkOrders;
